import { Button, Card, Typography } from '@prometheus/components'
import { Icon } from 'semantic-ui-react'

interface LobbyIdentityProps {
  signedIn: boolean
  guest: boolean
  displayName?: string | null
  /** A guest session is being minted or a play link redeemed. */
  busy?: boolean
  error?: string | null
  onSignIn: () => void
  onPlayAsGuest: () => void
  onEditProfile: () => void
  onShowPlayLink: () => void
}

const row: React.CSSProperties = {
  display: 'flex',
  gap: '8px',
  width: '100%',
  justifyContent: 'center',
  alignItems: 'center',
  flexWrap: 'wrap',
}

/**
 * Who you are in the lobby, and how to become someone.
 *
 * Three states: signed in, playing as a guest, or neither — and signing in is
 * never the only way forward, which is the point of the guest path.
 */
export const LobbyIdentity = ({
  signedIn,
  guest,
  displayName,
  busy,
  error,
  onSignIn,
  onPlayAsGuest,
  onEditProfile,
  onShowPlayLink,
}: LobbyIdentityProps) => {
  const name = (
    <Typography onClick={onEditProfile} className="editable-text" component="span" retro color="tertiary">
      {displayName || 'Anonymous'} <Icon size="small" name="pencil" />
    </Typography>
  )

  if (signedIn) {
    return <Typography retro align="center">Signed in as {name}</Typography>
  }

  if (guest) {
    return (
      <div style={{ width: '100%' }}>
        <div style={row}>
          <Card padding="none" borderColor="warning">
            <Typography color="warning" variant="caption" retro component="span">GUEST</Typography>
          </Card>
          <Typography retro align="center">Playing as {name}</Typography>
        </div>
        <div style={{ ...row, marginTop: '8px' }}>
          <Button size="sm" outline variant="warning" onClick={onShowPlayLink}>Save Play Link</Button>
          <Button size="sm" outline variant="secondary" onClick={onSignIn}>Sign In Instead</Button>
        </div>
        {!!error && <Typography variant="caption" align="center" color="danger">{error}</Typography>}
      </div>
    )
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={row}>
        <Typography retro align="center" color="danger">Not signed in</Typography>
        <Button size="sm" outline variant="danger" onClick={onSignIn}>Sign In</Button>
        <Button size="sm" outline variant="primary" loading={busy} disabled={busy} onClick={onPlayAsGuest}>
          Play as Guest
        </Button>
      </div>
      <Typography variant="caption" align="center" color="text-muted">
        No account needed — you’ll get a link back to your games.
      </Typography>
      {!!error && <Typography variant="caption" align="center" color="danger">{error}</Typography>}
    </div>
  )
}
